import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Overline, Reveal } from "./Reveal";
import Globe from "./Globe";

const LONDON_IMG =
  "https://images.unsplash.com/photo-1513026705753-bc3fffca8bf4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxsb25kb24lMjBjaXR5c2NhcGUlMjBuaWdodHxlbnwwfHx8fDE3ODcxOTgyMzl8MA&ixlib=rb-4.1.0&q=85";
const DUBAI_IMG =
  "https://images.unsplash.com/flagged/photo-1559717201-fbb671ff56b7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxkdWJhaSUyMHNreWxpbmUlMjBuaWdodHxlbnwwfHx8fDE3ODcxOTgyMjd8MA&ixlib=rb-4.1.0&q=85";

const CENTRES = [
  { city: "London", country: "United Kingdom", region: "EMEA", note: "Group headquarters — 4 Winsley Street, W1W 8HF" },
  { city: "Dubai", country: "United Arab Emirates", region: "EMEA", note: "Middle East enterprise delivery" },
  { city: "Singapore", country: "Singapore", region: "APAC", note: "Engineering & analytics hub" },
  { city: "Toronto", country: "Canada", region: "AMERICAS", note: "North American delivery" },
  { city: "Chicago", country: "Illinois, USA", region: "AMERICAS", note: "US operations & support" },
  { city: "Delhi NCR", country: "India", region: "APAC", note: "Engineering, analytics & backend delivery" },
];

export default function Footprint() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const yA = useTransform(scrollYProgress, [0, 1], [40, -40]);
  const yB = useTransform(scrollYProgress, [0, 1], [90, -60]);

  return (
    <section
      id="footprint"
      ref={ref}
      data-testid="footprint-section"
      className="mx-auto max-w-7xl scroll-mt-24 overflow-hidden px-6 py-24 lg:px-8 lg:py-32"
    >
      <Globe />

      <div className="grid gap-16 lg:grid-cols-12">
        <div className="lg:col-span-6">
          <Reveal>
            <Overline>Global Footprint</Overline>
            <h2 className="mt-6 font-heading text-4xl font-medium leading-[1.05] tracking-tight sm:text-5xl">
              Six centres. Three regions.{" "}
              <span className="text-gold">One standard.</span>
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-gray-400">
              24/5 follow-the-sun coverage across the Americas, EMEA and APAC —
              engagements delivered in 35+ countries through direct presence and a
              partner network.
            </p>
          </Reveal>

          <div className="mt-12 border-t border-white/10">
            {CENTRES.map((c, i) => (
              <Reveal key={c.city} delay={i * 0.05}>
                <div
                  data-testid={`centre-${c.city.toLowerCase().replace(/\s+/g, "-")}`}
                  className="group flex items-baseline justify-between gap-4 border-b border-white/10 py-5 transition-colors duration-300 hover:border-gold/40"
                >
                  <div>
                    <p className="font-heading text-xl font-medium tracking-tight text-white transition-colors duration-300 group-hover:text-gold">
                      {c.city}
                      <span className="ml-3 text-sm font-normal text-gray-500">{c.country}</span>
                    </p>
                    <p className="mt-1 text-xs text-gray-500">{c.note}</p>
                  </div>
                  <span className="shrink-0 font-mono text-[10px] uppercase tracking-[0.25em] text-gray-500">
                    {c.region}
                  </span>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-10 overflow-hidden border border-white/10 lg:hidden">
            <img
              src={LONDON_IMG}
              alt="London cityscape at night"
              className="aspect-video w-full object-cover"
              loading="lazy"
            />
          </div>
        </div>

        <div className="relative hidden lg:col-span-6 lg:block">
          <motion.figure
            style={{ y: yA }}
            className="absolute right-0 top-0 w-[72%] overflow-hidden border border-white/10"
          >
            <img src={LONDON_IMG} alt="London cityscape at night" className="aspect-[4/5] w-full object-cover" />
            <div className="absolute inset-0 bg-ink/30" />
            <figcaption className="absolute bottom-4 left-4 bg-ink/70 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-white backdrop-blur-md">
              London — HQ
            </figcaption>
          </motion.figure>
          <motion.figure
            style={{ y: yB }}
            className="absolute bottom-0 left-0 w-[52%] overflow-hidden border border-white/10"
          >
            <img src={DUBAI_IMG} alt="Dubai skyline at night" className="aspect-[3/4] w-full object-cover" />
            <div className="absolute inset-0 bg-ink/30" />
            <figcaption className="absolute bottom-4 left-4 bg-ink/70 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-white backdrop-blur-md">
              Dubai — EMEA
            </figcaption>
          </motion.figure>
          <div
            className="absolute right-[10%] top-[55%] border border-gold/30 bg-surface/80 px-5 py-4 backdrop-blur-xl"
            data-testid="footprint-coverage-badge"
          >
            <p className="font-heading text-3xl font-bold text-gold">24/5</p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-gray-400">
              Timezone coverage
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
